import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Video, Radio, Wallet, ShieldCheck, Sparkles, Share2 } from "lucide-react";
import { ShareProfileModal } from "@/components/ShareProfileModal";

export default function CreatorRegister() {
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const [share, setShare] = useState(false);
  const nav = useNavigate();

  const toggle = async (enabled) => {
    setLoading(true);
    try {
      await api.post("/creator/register", { enabled });
      await refresh();
      toast.success(enabled ? "You're registered as a Velluto Model" : "Creator status removed");
    } catch (e) {
      if (e.response?.status === 403) { nav("/apply"); return; }
      toast.error(e.response?.data?.detail || "Could not update creator status");
    } finally {
      setLoading(false);
    }
  };

  const isCreator = !!user?.is_creator;

  return (
    <div className="pt-24 pb-24 max-w-3xl mx-auto px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Content creators</p>
        <h1 className="font-display text-5xl md:text-6xl font-light mb-6">Become a Velluto Model</h1>
        <p className="text-text-muted leading-relaxed max-w-xl mb-10">
          Register to appear in <span className="text-text-main">Velluto Models</span> — go live for multi-viewer
          cam chat, or offer 1-on-1 pay-per-minute private sessions (C2C). You keep <span className="text-gold">80%</span>
          {" "}of every gift and private minute, auto-paid to your connected Stripe account.
        </p>

        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          {[
            { Icon: Radio, t: "Live streaming", d: "Multi-viewer cam rooms with live chat & gifting" },
            { Icon: Video, t: "Private C2C", d: "1-on-1 pay-per-minute video sessions" },
            { Icon: Wallet, t: "Instant payouts", d: "80/20 split, auto-transferred to your bank" },
          ].map(({ Icon, t, d }) => (
            <div key={t} className="rounded-2xl border border-white/10 bg-surface p-5">
              <Icon size={20} className="text-gold mb-3" strokeWidth={1.5} />
              <p className="font-display text-lg">{t}</p>
              <p className="text-xs text-text-muted mt-1">{d}</p>
            </div>
          ))}
        </div>

        <div data-testid="creator-status-card" className="rounded-3xl border border-gold/30 bg-surface p-8">
          {isCreator ? (
            <>
              <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-emerald-400 mb-4">
                <ShieldCheck size={15} /> You're a registered Velluto Model
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  data-testid="go-to-live-room"
                  onClick={() => nav(`/models/${user.id}/live`)}
                  className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-full bg-wine hover:bg-wine-hover transition-colors text-sm uppercase tracking-[0.2em]"
                >
                  <Sparkles size={15} /> Open my live room
                </button>
                <button
                  data-testid="share-profile-btn"
                  onClick={() => setShare(true)}
                  className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-full border border-gold/50 text-gold hover:bg-gold/10 transition-colors text-sm uppercase tracking-[0.2em]"
                >
                  <Share2 size={15} /> Share profile
                </button>
                <button
                  data-testid="unregister-creator"
                  disabled={loading}
                  onClick={() => toggle(false)}
                  className="py-3.5 px-6 rounded-full border border-white/15 hover:border-red-500/50 hover:text-red-400 transition-colors text-sm uppercase tracking-[0.2em] disabled:opacity-50"
                >
                  {loading ? "…" : "Unregister"}
                </button>
              </div>
              <p className="text-[11px] text-text-muted mt-4">
                Set your private-session rate & availability, and manage payouts from your{" "}
                <button onClick={() => nav("/wallet")} className="text-gold underline">Wallet</button>.
              </p>
            </>
          ) : (
            <>
              <p className="font-display text-2xl mb-2">Ready to go live?</p>
              <p className="text-sm text-text-muted mb-6">
                Registration is instant. You'll appear in the Velluto Models directory right away.
              </p>
              <button
                data-testid="register-creator"
                disabled={loading}
                onClick={() => toggle(true)}
                className="w-full py-3.5 rounded-full bg-gold text-ink hover:bg-gold/90 transition-colors text-sm uppercase tracking-[0.2em] disabled:opacity-50"
              >
                {loading ? "…" : "Register as a Creator/Model"}
              </button>
            </>
          )}
        </div>
      </motion.div>
      <ShareProfileModal open={share} onClose={() => setShare(false)} user={user} />
    </div>
  );
}
